import { Drawer, Modal, message } from "antd";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { CareShell } from "../../components/care/CareLayout";
import {
  emptyInventoryFilters,
  InventoryFilterPanel,
  inventoryFiltersFromSearchParams,
  syncInventoryFiltersToSearchParams,
} from "../../components/care/InventoryFilterPanel";
import VehicleImage from "../../components/care/VehicleImage";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";
import { VehicleListingDetails, listingDetailsFor } from "./VehicleDetailPage";
import { vehicleSelectOptions } from "../../constants/vehicleSelectOptions";

const heroPhoto = "/auto/dealer-inventory-hero.png";
const lotPhoto = heroPhoto;
const logoPhoto = heroPhoto;
const INVENTORY_PAGE_SIZE = 12;
const GUEST_HIDDEN_STATUS_TOKENS = new Set(["removed", "sold"]);
const paginationItems = (currentPage, totalPages) => {
  const pages = new Set([1, totalPages]);
  for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
    if (page > 1 && page < totalPages) pages.add(page);
  }
  return [...pages]
    .sort((a, b) => a - b)
    .reduce((items, page, index, sortedPages) => {
      if (index && page - sortedPages[index - 1] > 1) items.push("ellipsis");
      items.push(page);
      return items;
    }, []);
};

export const inventoryVehicles = [
  {
    id: "rav4-xle-awd",
    title: "2024 Toyota RAV4 XLE AWD",
    price: "$31,890",
    mileage: "14,210 mi",
    drivetrain: "AWD",
    fuel: "Gas",
    color: "Lunar Rock",
    status: "Fresh arrival",
    mainPhoto: lotPhoto,
    photos: [lotPhoto, heroPhoto, logoPhoto],
  },
  {
    id: "accord-ex",
    title: "2023 Honda Accord EX",
    price: "$27,450",
    mileage: "18,604 mi",
    drivetrain: "FWD",
    fuel: "Gas",
    color: "Platinum White",
    status: "Certified",
    mainPhoto: heroPhoto,
    photos: [heroPhoto, lotPhoto, logoPhoto],
  },
  {
    id: "f150-lariat",
    title: "2022 Ford F-150 Lariat",
    price: "$44,980",
    mileage: "33,112 mi",
    drivetrain: "4WD",
    fuel: "Gas",
    color: "Antimatter Blue",
    status: "Truck",
    mainPhoto: lotPhoto,
    photos: [lotPhoto, heroPhoto, logoPhoto],
  },
  {
    id: "ioniq-5-sel",
    title: "2025 Hyundai IONIQ 5 SEL",
    price: "$38,995",
    mileage: "6,420 mi",
    drivetrain: "RWD",
    fuel: "Electric",
    color: "Cyber Gray",
    status: "EV",
    mainPhoto: heroPhoto,
    photos: [heroPhoto, lotPhoto, logoPhoto],
  },
  {
    id: "silverado-lt",
    title: "2021 Chevrolet Silverado 1500 LT",
    price: "$36,750",
    mileage: "41,905 mi",
    drivetrain: "4WD",
    fuel: "Gas",
    color: "Summit White",
    status: "Ready",
    mainPhoto: lotPhoto,
    photos: [lotPhoto, heroPhoto, logoPhoto],
  },
  {
    id: "rogue-sv",
    title: "2020 Nissan Rogue SV AWD",
    price: "$19,995",
    mileage: "52,318 mi",
    drivetrain: "AWD",
    fuel: "Gas",
    color: "Gun Metallic",
    status: "Value pick",
    mainPhoto: heroPhoto,
    photos: [heroPhoto, lotPhoto, logoPhoto],
  },
  {
    id: "telluride-ex",
    title: "2022 Kia Telluride EX",
    price: "$34,480",
    mileage: "29,744 mi",
    drivetrain: "FWD",
    fuel: "Gas",
    color: "Gravity Gray",
    status: "Family SUV",
    mainPhoto: lotPhoto,
    photos: [lotPhoto, heroPhoto, logoPhoto],
  },
  {
    id: "outback-premium",
    title: "2023 Subaru Outback Premium",
    price: "$28,990",
    mileage: "21,386 mi",
    drivetrain: "AWD",
    fuel: "Gas",
    color: "Crystal Black",
    status: "AWD",
    mainPhoto: heroPhoto,
    photos: [heroPhoto, lotPhoto, logoPhoto],
  },
  {
    id: "charger-sxt",
    title: "2019 Dodge Charger SXT",
    price: "$22,995",
    mileage: "63,112 mi",
    drivetrain: "RWD",
    fuel: "Gas",
    color: "Destroyer Gray",
    status: "Sport sedan",
    mainPhoto: lotPhoto,
    photos: [lotPhoto, heroPhoto, logoPhoto],
  },
];

const pageItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const compactList = (...values) =>
  values.map((value) => String(value || "").trim()).filter(Boolean);

const titleFor = (vehicle = {}) =>
  firstText(
    vehicle.title,
    vehicle.name,
    compactList(vehicle.year, vehicle.make, vehicle.model, vehicle.trim).join(" "),
    "Vehicle",
  );

const isUrlLike = (value) =>
  /^(https?:|\/)/i.test(String(value || "").trim());

const directPhotoUrl = (value) => {
  if (typeof value === "string") return isUrlLike(value) ? value.trim() : "";
  if (value && typeof value === "object") {
    const url = firstText(
      value.signedUrl,
      value.url,
      value.photoUrl,
      value.imageUrl,
      value.src,
      value.downloadUrl,
      value.fileUrl,
      value.publicUrl,
    );
    return isUrlLike(url) ? url : "";
  }
  return "";
};

const photoSourcesFrom = (value) => {
  const values = Array.isArray(value) ? value : [value];
  return values
    .map(directPhotoUrl)
    .filter(Boolean);
};

const photoSourcesFor = (vehicle = {}, { includeAllPhotos = false } = {}) => [
  ...photoSourcesFrom(vehicle.mainPhoto),
  ...(includeAllPhotos ? photoSourcesFrom(vehicle.photos) : photoSourcesFrom(vehicle.photos).slice(0, 1)),
];

const statusToken = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const displayStatus = (value) => {
  const raw = firstText(value, "Ready");
  return statusToken(raw) === "ready" ? "Available" : raw;
};

const cleanSelectOptionValue = (value) =>
  String(value || "").replace(/\s*[[(][^\])]*[\])]\s*$/g, "").trim();

const normalizeVehicle = (vehicle = {}, options = {}) => {
  const normalizeOptions = options && typeof options === "object" && !Array.isArray(options) ? options : {};
  const resolvedPhotos = [...new Set(photoSourcesFor(vehicle, normalizeOptions))];
  const photos = resolvedPhotos;
  const status = displayStatus(vehicle.status);
  return {
    ...vehicle,
    id: vehicle.id || vehicle.vehicleId || vehicle.vin || titleFor(vehicle),
    title: titleFor(vehicle),
    price: formatPrice(firstText(vehicle.price, vehicle.askingPrice, vehicle.listPrice)),
    mileage: formatMileage(vehicle.mileage),
    make: cleanSelectOptionValue(firstText(vehicle.make)),
    model: cleanSelectOptionValue(firstText(vehicle.model)),
    drivetrain: cleanSelectOptionValue(firstText(vehicle.drivetrain)) || "Drivetrain pending",
    engine: cleanSelectOptionValue(firstText(vehicle.engine)),
    transmission: cleanSelectOptionValue(firstText(vehicle.transmission)),
    fuel: cleanSelectOptionValue(firstText(vehicle.fuelType, vehicle.fuel)) || "Fuel pending",
    color: cleanSelectOptionValue(firstText(vehicle.exteriorColor, vehicle.color)) || "Color pending",
    interiorColor: cleanSelectOptionValue(firstText(vehicle.interiorColor)),
    exteriorColor: cleanSelectOptionValue(firstText(vehicle.exteriorColor, vehicle.color)),
    status,
    statusKey: statusToken(status),
    yearValue: numberFrom(vehicle.year) || 0,
    priceValue: numberFrom(vehicle.price) || 0,
    mileageValue: numberFrom(vehicle.mileage) || 0,
    createdAt: firstText(vehicle.createdAt, vehicle.createdDate, vehicle.createdOn, vehicle.updatedAt),
    mainPhoto: photos[0],
    photos,
  };
};

const matchesFilter = (vehicle, filter) => {
  if (filter === "all") return true;
  const status = statusToken(vehicle.status);
  const body = statusToken(vehicle.bodyType);
  const fuel = statusToken(vehicle.fuel);
  if (filter === "fresh") return status === "fresh_arrival" || status === "incoming" || status === "upcoming";
  if (filter === "suv") return body.includes("suv") || body.includes("crossover");
  if (filter === "truck") return body.includes("truck");
  if (filter === "ev") return fuel.includes("electric") || fuel.includes("hybrid");
  return true;
};

const searchableText = (vehicle = {}) =>
  compactList(
    vehicle.title,
    vehicle.year,
    vehicle.make,
    vehicle.model,
    vehicle.trim,
    vehicle.bodyType,
    vehicle.condition,
    vehicle.price,
    vehicle.mileage,
    vehicle.vin,
    vehicle.status,
    vehicle.exteriorColor,
    vehicle.interiorColor,
    vehicle.drivetrain,
    vehicle.transmission,
    vehicle.engine,
    vehicle.fuel,
    vehicle.color,
  ).join(" ").toLowerCase();

const formatPrice = (value) => {
  const text = String(value || "").trim();
  if (!text) return "Any price";
  if (/^\$/.test(text)) return text;
  const number = Number(text.replace(/[^0-9.]/g, ""));
  return Number.isFinite(number) && number > 0 ? `$${number.toLocaleString("en-US")}` : text;
};

const numberFrom = (value) => {
  const normalized = String(value || "").replace(/[^0-9.]/g, "");
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatMileage = (value) => {
  const text = String(value || "").trim();
  if (!text) return "Mileage pending";
  const number = numberFrom(text);
  return number !== null && number > 0 ? `${number.toLocaleString("en-US")} miles` : text;
};

const uniqueOptions = (vehicles, key) =>
  [...new Set(vehicles.map((vehicle) => firstText(vehicle[key])).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));

const pageMetaFor = (value, items, requestedPage, fallbackSize = INVENTORY_PAGE_SIZE) => {
  const pageSize = Number(value?.size ?? value?.page?.size ?? fallbackSize) || fallbackSize;
  const totalElements = Number(value?.totalElements ?? value?.total ?? value?.page?.totalElements ?? items.length) || 0;
  const totalPages = Math.max(1, Number(value?.totalPages ?? value?.page?.totalPages ?? Math.ceil(totalElements / pageSize)) || 1);
  const pageNumber = Number(value?.number ?? value?.pageNumber ?? value?.page?.number ?? requestedPage) || 0;
  return {
    number: pageNumber,
    size: pageSize,
    totalElements,
    totalPages,
    first: value?.first ?? pageNumber <= 0,
    last: value?.last ?? pageNumber >= totalPages - 1,
  };
};

const backendFilterParams = (filters = {}) => {
  const params = {};
  const setText = (key, value) => {
    const cleaned = cleanSelectOptionValue(value);
    if (cleaned) params[key] = cleaned;
  };
  const statusValue = cleanSelectOptionValue(filters.status);
  const statusEnum = statusValue
    ? (statusToken(displayStatus(statusValue)) === "available" ? "READY" : statusToken(statusValue).toUpperCase())
    : "";
  setText("q", filters.query);
  setText("search", filters.query);
  setText("make", filters.make);
  setText("model", filters.model);
  setText("engine", filters.engine);
  setText("fuelType", filters.fuel);
  setText("fuel", filters.fuel);
  setText("drivetrain", filters.drivetrain);
  setText("transmission", filters.transmission);
  if (statusValue) {
    params.status = statusValue;
    params.inventoryStatus = statusEnum;
    params.statusKey = statusToken(displayStatus(statusValue));
  }
  setText("exteriorColor", filters.exteriorColor);
  setText("interiorColor", filters.interiorColor);
  if (filters.minYear) params.minYear = filters.minYear;
  if (filters.maxYear) params.maxYear = filters.maxYear;
  if (filters.minPrice) params.minPrice = filters.minPrice;
  if (filters.maxPrice) params.maxPrice = filters.maxPrice;
  if (filters.minMileage) params.minMileage = filters.minMileage;
  if (filters.maxMileage) params.maxMileage = filters.maxMileage;
  return params;
};

export default function VehiclesListPage() {
  const { user } = useAuth();
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const actorId = user?.id || user?.userId || "";
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(() => inventoryFiltersFromSearchParams(searchParams));
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [inventoryPage, setInventoryPage] = useState(Number(searchParams.get("page") || 0) || 0);
  const [inventoryPageMeta, setInventoryPageMeta] = useState({
    number: 0,
    size: INVENTORY_PAGE_SIZE,
    totalElements: 0,
    totalPages: 1,
    first: true,
    last: true,
  });
  const [selectedPhotoByVehicleId, setSelectedPhotoByVehicleId] = useState({});
  const [detailVehicle, setDetailVehicle] = useState(null);
  const [detailVehicleLoading, setDetailVehicleLoading] = useState(false);
  const [detailVehicleError, setDetailVehicleError] = useState("");
  const [vehicleActionLoading, setVehicleActionLoading] = useState("");
  const isLoggedInUser = Boolean(actorId);

  useEffect(() => {
    let active = true;

    const loadVehicles = async () => {
      if (!workspaceListReady) return;

      setLoading(true);
      setError("");

      try {
        const workspaceParams = {
          ...(selectedWorkspaceId ? { wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId } : {}),
          ...backendFilterParams(filters),
          actorId,
          page: inventoryPage,
          size: INVENTORY_PAGE_SIZE,
          sort: "createdAt,desc",
        };
        const data = await careService.vehicles.listPage(workspaceParams);
        if (!active) return;
        const nextVehicles = pageItems(data)
          .map(normalizeVehicle)
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setInventoryPageMeta(pageMetaFor(data, nextVehicles, inventoryPage));
        setVehicles(nextVehicles);
        setSelectedPhotoByVehicleId((current) => {
          const next = {};
          nextVehicles.forEach((vehicle) => {
            const selectedPhoto = current[vehicle.id];
            next[vehicle.id] = selectedPhoto && vehicle.mainPhoto === selectedPhoto
              ? selectedPhoto
              : vehicle.mainPhoto;
          });
          return next;
        });
      } catch (nextError) {
        if (!active) return;
        setVehicles([]);
        setInventoryPageMeta(pageMetaFor(null, [], inventoryPage));
        setError(nextError?.response?.data?.message || nextError?.message || "Unable to load vehicles.");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadVehicles();

    return () => {
      active = false;
    };
  }, [actorId, filters, inventoryPage, selectedWorkspaceId, workspaceListReady]);

  useEffect(() => {
    const nextFilters = inventoryFiltersFromSearchParams(searchParams);
    setFilters((current) => (
      JSON.stringify(current) === JSON.stringify(nextFilters) ? current : nextFilters
    ));
    const nextPage = Number(searchParams.get("page") || 0) || 0;
    setInventoryPage((current) => (current === nextPage ? current : nextPage));
  }, [searchParams]);

  const filteredVehicles = useMemo(() => {
    const needle = filters.query.trim().toLowerCase();
    const activeStatusToken = filters.status ? statusToken(displayStatus(filters.status)) : "";
    return vehicles.filter((vehicle) => {
      const searchMatches = !needle || searchableText(vehicle).includes(needle);
      if (!searchMatches) return false;
      if (filters.make && vehicle.make !== cleanSelectOptionValue(filters.make)) return false;
      if (filters.model && vehicle.model !== cleanSelectOptionValue(filters.model)) return false;
      if (filters.engine && vehicle.engine !== cleanSelectOptionValue(filters.engine)) return false;
      if (filters.fuel && vehicle.fuel !== cleanSelectOptionValue(filters.fuel)) return false;
      if (filters.drivetrain && vehicle.drivetrain !== cleanSelectOptionValue(filters.drivetrain)) return false;
      if (filters.transmission && vehicle.transmission !== cleanSelectOptionValue(filters.transmission)) return false;
      if (activeStatusToken && vehicle.statusKey !== activeStatusToken) return false;
      if (filters.exteriorColor && vehicle.exteriorColor !== cleanSelectOptionValue(filters.exteriorColor)) return false;
      if (filters.interiorColor && vehicle.interiorColor !== cleanSelectOptionValue(filters.interiorColor)) return false;
      const year = numberFrom(vehicle.year);
      const minYear = numberFrom(filters.minYear);
      const maxYear = numberFrom(filters.maxYear);
      const price = numberFrom(vehicle.price);
      const minPrice = numberFrom(filters.minPrice);
      const maxPrice = numberFrom(filters.maxPrice);
      const mileage = numberFrom(vehicle.mileage);
      const minMileage = numberFrom(filters.minMileage);
      const maxMileage = numberFrom(filters.maxMileage);
      if (minYear !== null && (year === null || year < minYear)) return false;
      if (maxYear !== null && (year === null || year > maxYear)) return false;
      if (minPrice !== null && (price === null || price < minPrice)) return false;
      if (maxPrice !== null && (price === null || price > maxPrice)) return false;
      if (minMileage !== null && (mileage === null || mileage < minMileage)) return false;
      if (maxMileage !== null && (mileage === null || mileage > maxMileage)) return false;
      return true;
    });
  }, [filters, vehicles]);

  const filterOptions = useMemo(() => ({
    makes: uniqueOptions(vehicles, "make"),
    models: uniqueOptions(
      filters.make ? vehicles.filter((vehicle) => vehicle.make === cleanSelectOptionValue(filters.make)) : vehicles,
      "model",
    ),
    years: uniqueOptions(vehicles, "year").sort((a, b) => Number(b) - Number(a)),
    engines: uniqueOptions(vehicles, "engine"),
    fuels: uniqueOptions(vehicles, "fuel"),
    drivetrains: uniqueOptions(vehicles, "drivetrain"),
    transmissions: uniqueOptions(vehicles, "transmission"),
    statuses: uniqueOptions(vehicles, "status"),
    exteriorColors: uniqueOptions(vehicles, "exteriorColor"),
    interiorColors: uniqueOptions(vehicles, "interiorColor"),
  }), [filters.make, vehicles]);

  const maxVehiclePrice = useMemo(
    () => Math.max(0, ...vehicles.map((vehicle) => numberFrom(vehicle.price) || 0)),
    [vehicles],
  );
  const maxVehicleMileage = useMemo(
    () => Math.max(0, ...vehicles.map((vehicle) => numberFrom(vehicle.mileage) || 0)),
    [vehicles],
  );
  const activeMinPrice = numberFrom(filters.minPrice) ?? 0;
  const activeMaxPrice = numberFrom(filters.maxPrice) ?? maxVehiclePrice;
  const activeMinMileage = numberFrom(filters.minMileage) ?? 0;
  const activeMaxMileage = numberFrom(filters.maxMileage) ?? maxVehicleMileage;
  const inventoryTotal = inventoryPageMeta.totalElements || filteredVehicles.length;
  const inventoryTotalPages = Math.max(1, inventoryPageMeta.totalPages || Math.ceil(inventoryTotal / INVENTORY_PAGE_SIZE));
  const safeInventoryPage = Math.max(0, Math.min(inventoryPage, inventoryTotalPages - 1));
  const inventoryCurrentPage = safeInventoryPage + 1;
  const inventoryStartIndex = safeInventoryPage * (inventoryPageMeta.size || INVENTORY_PAGE_SIZE);
  const inventoryStart = inventoryTotal && filteredVehicles.length ? inventoryStartIndex + 1 : 0;
  const inventoryEnd = inventoryTotal ? Math.min(inventoryTotal, inventoryStartIndex + filteredVehicles.length) : 0;
  const inventoryPageItems = paginationItems(inventoryCurrentPage, inventoryTotalPages);
  const paginatedVehicles = filteredVehicles;
  const hasActiveFilters = Object.values(filters).some(Boolean);
  const commitFilters = (nextFilters) => {
    setFilters(nextFilters);
    setInventoryPage(0);
    setSearchParams(syncInventoryFiltersToSearchParams(searchParams, nextFilters, { deleteKeys: ["page"] }), { replace: true });
  };
  const updateInventoryFilter = (field, value) => {
    commitFilters({
      ...filters,
      [field]: value,
      ...(field === "make" ? { model: "" } : {}),
    });
  };
  const resetFilters = () => {
    commitFilters({ ...emptyInventoryFilters });
  };
  const goToInventoryPage = (page) => {
    const nextPage = Math.max(0, Math.min(inventoryTotalPages - 1, page));
    const nextSearchParams = new URLSearchParams(searchParams);
    if (nextPage) {
      nextSearchParams.set("page", String(nextPage));
    } else {
      nextSearchParams.delete("page");
    }
    setInventoryPage(nextPage);
    setSearchParams(nextSearchParams, { replace: true });
  };

  const closeVehicleDetails = () => {
    setDetailVehicle(null);
    setDetailVehicleLoading(false);
    setDetailVehicleError("");
    setVehicleActionLoading("");
  };

  const openVehicleDetails = async (vehicle) => {
    setDetailVehicle(vehicle);
    setDetailVehicleLoading(true);
    setDetailVehicleError("");
    try {
      const fullVehicle = await careService.vehicles.get(vehicle.id);
      setDetailVehicle(normalizeVehicle({ ...vehicle, ...(fullVehicle || {}) }, { includeAllPhotos: true }));
    } catch (nextError) {
      setDetailVehicleError(nextError?.response?.data?.message || nextError?.message || "Unable to load full vehicle details.");
    } finally {
      setDetailVehicleLoading(false);
    }
  };

  const patchDrawerVehicleStatus = async (nextStatus, successMessage) => {
    if (!detailVehicle?.id || vehicleActionLoading) return;
    setVehicleActionLoading(nextStatus);
    try {
      const updated = await careService.vehicles.patch(detailVehicle.id, {
        status: nextStatus,
        inventoryStatus: nextStatus,
      });
      const mergedVehicle = {
        ...detailVehicle,
        ...(updated || {}),
        status: updated?.status || nextStatus,
        inventoryStatus: updated?.inventoryStatus || nextStatus,
      };
      const nextDetailVehicle = normalizeVehicle(mergedVehicle, { includeAllPhotos: true });
      const nextListVehicle = normalizeVehicle(mergedVehicle);
      setDetailVehicle(nextDetailVehicle);
      setVehicles((current) => current.map((vehicle) => (vehicle.id === nextListVehicle.id ? nextListVehicle : vehicle)));
      message.success(successMessage);
    } catch (nextError) {
      message.error(nextError?.response?.data?.message || nextError?.message || "Unable to update vehicle.");
    } finally {
      setVehicleActionLoading("");
    }
  };

  const handleDrawerManagementAction = (action) => {
    if (!detailVehicle?.id) return;
    if (action === "update") {
      navigate(`/vehicles/${detailVehicle.id}/update`);
      return;
    }
    const vehicleName = detailVehicle.title || "Vehicle";
    if (action === "sold") {
      Modal.confirm({
        title: "Mark vehicle as sold?",
        content: `${vehicleName} will be marked as sold and removed from active availability.`,
        okText: "Mark Sold",
        cancelText: "Cancel",
        centered: true,
        onOk: () => patchDrawerVehicleStatus("SOLD", `${vehicleName} marked as sold.`),
      });
      return;
    }
    if (action === "pending") {
      Modal.confirm({
        title: "Mark vehicle as pending?",
        content: `${vehicleName} will be marked as pending for review or preparation.`,
        okText: "Mark Pending",
        cancelText: "Cancel",
        centered: true,
        onOk: () => patchDrawerVehicleStatus("PENDING", `${vehicleName} marked as pending.`),
      });
      return;
    }
    if (action === "active") {
      Modal.confirm({
        title: "Mark vehicle as active?",
        content: `${vehicleName} will be marked as ready and available in inventory.`,
        okText: "Mark Active",
        cancelText: "Cancel",
        centered: true,
        onOk: () => patchDrawerVehicleStatus("READY", `${vehicleName} marked as active.`),
      });
      return;
    }
    if (action === "remove") {
      Modal.confirm({
        title: "Remove vehicle from inventory?",
        content: `${vehicleName} will be marked as removed and no longer treated as active inventory.`,
        okText: "Remove Vehicle",
        cancelText: "Cancel",
        centered: true,
        onOk: () => patchDrawerVehicleStatus("REMOVED", `${vehicleName} removed from active inventory.`),
      });
      return;
    }
    if (action === "delete") {
      Modal.confirm({
        title: "Delete vehicle?",
        content: `Open the full vehicle page to permanently delete ${vehicleName}.`,
        okText: "Open Full Page",
        cancelText: "Cancel",
        centered: true,
        onOk: () => navigate(`/vehicles/${detailVehicle.id}`),
      });
    }
  };

  const handleDrawerSectionAction = (sectionKey) => {
    if (!detailVehicle?.id) return;
    navigate(`/vehicles/${detailVehicle.id}#${sectionKey}`);
  };

  const freshCount = vehicles.filter((vehicle) => matchesFilter(vehicle, "fresh")).length;
  const alternativeFuelCount = vehicles.filter((vehicle) => matchesFilter(vehicle, "ev")).length;
  const awdCount = vehicles.filter((vehicle) => /(^|\b)(awd|4wd|4x4|four-wheel|all-wheel)(\b|$)/i.test(vehicle.drivetrain)).length;
  const statusFilterOptions = useMemo(() => {
    const statuses = [
      ...vehicleSelectOptions.statuses,
      "Removed",
      ...filterOptions.statuses,
    ].map(displayStatus).filter(Boolean).filter((status) => (
      isLoggedInUser || !GUEST_HIDDEN_STATUS_TOKENS.has(statusToken(status))
    )).reduce((options, status) => {
      const token = statusToken(status);
      return options.some((option) => statusToken(option) === token) ? options : [...options, status];
    }, []);
    return [
      { label: "All", value: "", count: vehicles.length },
      ...statuses.map((status) => ({
        label: status,
        value: status,
        count: vehicles.filter((vehicle) => vehicle.statusKey === statusToken(status)).length,
      })),
    ];
  }, [filterOptions.statuses, isLoggedInUser, vehicles]);

  return (
    <CareShell className="dashboard-content vehicles-page">
      <div className="vehicles-page-head">
        <div className="page-title">
          <span className="section-eyebrow">Inventory</span>
          <h1>Vehicles</h1>
          <p>Browse active dealership inventory, review photos, and open each vehicle record.</p>
        </div>
        <div className="vehicles-actions">
          <button className="mini-btn" type="button" onClick={resetFilters}>
            <i className="ti ti-refresh" /> Reset Filters
          </button>
          <Link className="btn primary add-vehicle-button" to="/vehicles/new"><i className="ti ti-car-plus" /> Add Vehicle</Link>
        </div>
      </div>

      <div className="vehicle-summary-grid">
        {[
          ["ti-car", "Total vehicles", loading ? "..." : inventoryTotal, "Active inventory"],
          ["ti-sparkles", "Fresh arrivals", loading ? "..." : freshCount, "Added recently"],
          ["ti-bolt", "EV / Hybrid", loading ? "..." : alternativeFuelCount, "Alternative fuel"],
          ["ti-steering-wheel", "AWD / 4WD", loading ? "..." : awdCount, "All-weather options"],
        ].map(([icon, label, value, note]) => (
          <article className="vehicle-summary-card" key={label}>
            <span className="tiny-icon"><i className={`ti ${icon}`} /></span>
            <div><strong>{label}</strong><b>{value}</b><small>{note}</small></div>
          </article>
        ))}
      </div>

      {error ? <p className="form-error">{error}</p> : null}

      <label className="auto-hero-search vehicles-inline-search">
        <i className="ti ti-search" />
        <input
          aria-label="Search inventory"
          placeholder="Search make, model, trim, color, body style, or feature"
          value={filters.query}
          onChange={(event) => updateInventoryFilter("query", event.target.value)}
        />
      </label>

      <button
        className="auto-mobile-filter-toggle"
        type="button"
        onClick={() => setMobileFiltersOpen((isOpen) => !isOpen)}
        aria-controls="vehicles-inventory-filters"
        aria-expanded={mobileFiltersOpen}
      >
        <i className="ti ti-adjustments-horizontal" />
        {mobileFiltersOpen ? "Hide filters" : "Filter"}
      </button>

      <div
        className={`auto-inventory-layout logged-in-inventory-layout${mobileFiltersOpen ? " filters-open" : ""}`}
        id="vehicles-inventory-filters"
      >
        <InventoryFilterPanel
          activeMinMileage={activeMinMileage || 0}
          activeMaxMileage={activeMaxMileage || 0}
          activeMinPrice={activeMinPrice || 0}
          activeMaxPrice={activeMaxPrice || 0}
          filterOptions={filterOptions}
          filters={filters}
          hasActiveFilters={hasActiveFilters}
          maxMileage={maxVehicleMileage}
          maxPrice={maxVehiclePrice}
          matchCount={inventoryTotal}
          onReset={resetFilters}
          onUpdateFilter={updateInventoryFilter}
          priceLabel={formatPrice}
          statusFilterOptions={statusFilterOptions}
        />

        <div className="auto-inventory-grid">
          {loading ? (
            <p className="auto-inventory-empty">Loading vehicles...</p>
          ) : paginatedVehicles.length ? paginatedVehicles.map((vehicle) => {
            const selectedPhoto = selectedPhotoByVehicleId[vehicle.id] || vehicle.mainPhoto;
            return (
              <article className="auto-inventory-card" key={vehicle.id}>
                <div className="auto-car-visual">
                  <button
                    className="auto-car-visual-button"
                    type="button"
                    onClick={() => openVehicleDetails(vehicle)}
                    aria-label={`Open ${vehicle.title} details`}
                  >
                    <VehicleImage src={selectedPhoto} alt={vehicle.title} />
                  </button>
                </div>
                <div className="auto-inventory-card-body">
                  <span className={`auto-inventory-status is-${vehicle.statusKey || "ready"}`}>{vehicle.status}</span>
                  <div className="auto-inventory-card-title">
                    <div className="auto-card-icon-actions" aria-label={`${vehicle.title} actions`}>
                      <Link className="auto-card-detail-link" to={`/vehicles/${vehicle.id}`} aria-label={`View ${vehicle.title} details`}>
                        <i className="ti ti-external-link" />
                      </Link>
                      <Link className="auto-card-contact-icon" to={`/vehicles/${vehicle.id}/update`} aria-label={`Update ${vehicle.title}`}>
                        <i className="ti ti-pencil" />
                      </Link>
                    </div>
                  </div>
                  <button className="auto-inventory-card-details" type="button" onClick={() => openVehicleDetails(vehicle)} aria-label={`Open ${vehicle.title} details`}>
                    <h3>{vehicle.title}</h3>
                    <strong>{vehicle.price}</strong>
                    <p>{vehicle.mileage} - {vehicle.exteriorColor || vehicle.color}</p>
                    <small>{compactList(vehicle.engine, vehicle.transmission, vehicle.drivetrain).join(" - ") || vehicle.fuel}</small>
                  </button>
                </div>
              </article>
            );
          }) : (
            <p className="auto-inventory-empty">No vehicle records found for this workspace.</p>
          )}
        </div>
      </div>

      {!loading && inventoryTotal > INVENTORY_PAGE_SIZE ? (
        <div className="timesheet-pagination auto-inventory-pagination">
          <span>
            Showing <strong>{inventoryStart}-{inventoryEnd}</strong> of <strong>{inventoryTotal}</strong>
          </span>
          <div className="timesheet-pagination-actions">
            <button
              className="mini-btn"
              type="button"
              onClick={() => goToInventoryPage(safeInventoryPage - 1)}
              disabled={inventoryCurrentPage <= 1}
            >
              <i className="ti ti-chevron-left" /> Previous
            </button>
            <div className="inventory-page-number-list" aria-label="Inventory pages">
              {inventoryPageItems.map((page, index) => (
                page === "ellipsis" ? (
                  <span className="inventory-page-ellipsis" key={`ellipsis-${index}`}>...</span>
                ) : (
                  <button
                    aria-current={page === inventoryCurrentPage ? "page" : undefined}
                    aria-label={`Go to page ${page}`}
                    className={`inventory-page-number${page === inventoryCurrentPage ? " is-active" : ""}`}
                    disabled={page === inventoryCurrentPage}
                    key={page}
                    onClick={() => goToInventoryPage(page - 1)}
                    type="button"
                  >
                    {page}
                  </button>
                )
              ))}
            </div>
            <button
              className="mini-btn"
              type="button"
              onClick={() => goToInventoryPage(safeInventoryPage + 1)}
              disabled={inventoryCurrentPage >= inventoryTotalPages}
            >
              Next <i className="ti ti-chevron-right" />
            </button>
          </div>
        </div>
      ) : null}

      <Drawer
        className="auto-vehicle-detail-drawer"
        onClose={closeVehicleDetails}
        open={Boolean(detailVehicle)}
        placement="right"
        title={detailVehicle ? detailVehicle.title : "Vehicle details"}
        width="min(920px, 96vw)"
      >
        {detailVehicle ? (
          <div className="auto-vehicle-detail-drawer__content">
            {detailVehicleLoading ? (
              <div className="auto-vehicle-detail-loading" role="status" aria-live="polite">
                <i className="ti ti-loader-2" />
                <span>Loading full vehicle details...</span>
              </div>
            ) : null}
            <div className="auto-vehicle-detail-drawer__summary">
              <span>{detailVehicle.status}</span>
              <h2>{detailVehicle.title}</h2>
              <strong>{detailVehicle.price}</strong>
              <p>{detailVehicle.mileage} - {detailVehicle.exteriorColor || detailVehicle.color}</p>
              <small>{compactList(detailVehicle.engine, detailVehicle.transmission, detailVehicle.drivetrain).join(" - ") || detailVehicle.fuel}</small>
              <div className="auto-vehicle-detail-drawer__actions">
                <Link className="btn primary" to={`/vehicles/${detailVehicle.id}`}>
                  <i className="ti ti-external-link" /> Full page
                </Link>
                <Link className="btn secondary" to={`/vehicles/${detailVehicle.id}/update`}>
                  <i className="ti ti-pencil" /> Update
                </Link>
              </div>
            </div>
            <VehicleListingDetails
              details={detailVehicle.listingDetails || listingDetailsFor(detailVehicle)}
              managementActionLoading={vehicleActionLoading}
              onAction={handleDrawerSectionAction}
              onManagementAction={handleDrawerManagementAction}
              showMarkActive={statusToken(detailVehicle.status) !== "available"}
              vehicle={{
                ...detailVehicle,
                mainPhoto: detailVehicle.mainPhoto,
                name: detailVehicle.title,
                vehicleName: detailVehicle.title,
              }}
            />
            {detailVehicleError ? (
              <p className="form-error">{detailVehicleError}</p>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </CareShell>
  );
}
